import { useQueryClient } from "@tanstack/react-query";
import { EyeOff, Save, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArtifactPanel, collectArtifacts } from "@/components/chat/Artifacts";
import { AttachButton, DropZone, PendingFiles } from "@/components/chat/Attachments";
import Composer, { ModelSelect, useDefaultModel, useModels } from "@/components/chat/Composer";
import MessageView from "@/components/chat/MessageView";
import { chatRenderers } from "@/components/chat/renderers";
import FolderPicker from "@/components/chat/FolderPicker";
import ToolsPicker from "@/components/chat/ToolsPicker";
import { useStickToBottom } from "@/components/chat/useStickToBottom";
import { Button, ErrorText, Spinner } from "@/components/ui";
import { contextLength, usedTokens } from "@/lib/context";
import { api, ApiError } from "@/lib/api";
import { uploadFile, usePendingFiles } from "@/lib/files";
import { useLive } from "@/lib/live";
import { usePageTitle } from "@/lib/tabStatus";
import { clientId, onEvent } from "@/lib/ws";
import { blobToBase64, fetchLocalFile, isLocal, localFiles, localIds, newId, replaceIds, tempChats, type TempChat, type TempMessage } from "@/lib/temp";
import { PrivateToggle } from "./Home";
import type { Chat, MessageStatus, Part } from "@/lib/types";

function notifyChanged() {
  window.dispatchEvent(new Event("temp-chats-changed"));
}

export default function TempChatPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [chat, setChat] = useState<TempChat | null | undefined>(undefined);
  const [model, setModel] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const defaultModel = useDefaultModel(chat?.model_id);
  const modelId = model ?? defaultModel;
  const liveId = useLive((s) => s.byTemp[id]);
  const gen = useLive((s) => (liveId ? s.live[liveId] : undefined));
  const pending = usePendingFiles(!!chat?.private);
  usePageTitle(chat ? `Temporary: ${chat.title || "chat"}` : null);
  const models = useModels();
  const ctxLen = contextLength(models.data?.find((m) => m.id === modelId));
  const lastSave = useRef(0);
  const started = useRef(false);

  const save = useCallback(async (c: TempChat) => {
    setChat(c);
    await tempChats.put(c);
    notifyChanged();
  }, []);

  const generate = useCallback(
    async (c: TempChat) => {
      setError(null);
      const history = c.messages.filter((m) => m.status !== "streaming" && (m.role === "user" || m.parts.length));
      try {
        // Files kept in this browser go along with every request; the server holds them only
        // in memory while it answers.
        const files: Record<string, { name: string; mime: string; data: string }> = {};
        for (const fid of localIds(history)) {
          const f = await localFiles.get(fid);
          if (f) files[fid] = { name: f.name, mime: f.mime, data: await blobToBase64(f.blob) };
        }
        const r = await api.post<{ message_id: string; model_name: string }>("/api/temp/generate", {
          temp_id: c.id,
          client_id: clientId(),
          model_id: modelId,
          mcp_servers: c.mcp_servers ?? null,
          folder_id: c.folder_id ?? null,
          system_prompt: c.system_prompt ?? null,
          private: !!c.private,
          files,
          messages: history.map((m) => ({ role: m.role, parts: m.parts })),
        });
        const placeholder: TempMessage = { id: r.message_id, role: "assistant", parts: [], status: "streaming", model_name: r.model_name };
        await save({ ...c, model_id: modelId, messages: [...history, placeholder] });
      } catch (e) {
        setError(e);
      }
    },
    [modelId, save],
  );

  /** Asks the server about a reply that is still marked as streaming here (after a reload or reconnect). */
  const recover = useCallback(
    async (c: TempChat) => {
      const last = c.messages[c.messages.length - 1];
      if (last?.role !== "assistant" || last.status !== "streaming") return;
      try {
        const g = await api.get<{ message_id: string; seq: number; status: MessageStatus; parts: Part[]; error: string | null }>(`/api/temp/${id}/generation`);
        useLive.getState().apply({ type: "gen.parts", message_id: g.message_id, temp_id: id, seq: g.seq, parts: g.parts });
        if (g.status !== "streaming")
          useLive.getState().apply({ type: "gen.done", message_id: g.message_id, temp_id: id, seq: g.seq, parts: g.parts, status: g.status, error: g.error });
        useLive.setState((s) => ({ byTemp: { ...s.byTemp, [id]: g.message_id } }));
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) {
          const msgs = c.messages.map((m, i) =>
            i === c.messages.length - 1 ? { ...m, status: "error" as const, error: "The reply was lost (the server restarted or it finished too long ago)." } : m,
          );
          await save({ ...c, messages: msgs });
          if (last.id) useLive.getState().forget(last.id);
        }
      }
    },
    [id, save],
  );

  // Load from IndexedDB; recover a generation that was running when the page was reloaded.
  useEffect(() => {
    started.current = false;
    let alive = true;
    tempChats.get(id).then(async (c) => {
      if (!alive) return;
      setChat(c ?? null);
      if (c) await recover(c);
    });
    return () => {
      alive = false;
    };
  }, [id, recover]);

  // After the event connection comes back (e.g. a server restart), check again.
  useEffect(
    () =>
      onEvent((ev) => {
        if (ev.type === "reconnected") tempChats.get(id).then((c) => c && recover(c));
      }),
    [id, recover],
  );

  // A new chat from the home page arrives with only the user message: answer it. Only then:
  // replies to later messages are started by send/edit themselves (doing it here as well made
  // two parallel generations).
  useEffect(() => {
    if (!chat || started.current || !modelId || !(location.state as { autostart?: boolean } | null)?.autostart) return;
    started.current = true;
    // Forget the flag, so a reload does not answer again.
    navigate(location.pathname, { replace: true, state: null });
    const last = chat.messages[chat.messages.length - 1];
    if (last?.role === "user") generate(chat);
  }, [chat, modelId, generate, location, navigate]);

  // Mirror the live generation into the stored chat (throttled while streaming).
  useEffect(() => {
    if (!chat || !gen || !liveId) return;
    const idx = chat.messages.findIndex((m) => m.id === liveId);
    if (idx < 0) return;
    const done = gen.status !== "streaming";
    if (!done && Date.now() - lastSave.current < 1000) return;
    lastSave.current = Date.now();
    const msg = { ...chat.messages[idx], parts: gen.parts, status: gen.status, error: gen.error, usage: gen.usage };
    const messages = chat.messages.map((m, i) => (i === idx ? msg : m));
    if (done) {
      save({ ...chat, messages });
      useLive.getState().forget(liveId);
      // Files tools made in a private chat that did not arrive by event (e.g. after a reconnect).
      for (const p of gen.parts)
        if (p.type === "tool_call")
          for (const a of p.result ?? [])
            if (a.type === "attachment" && isLocal(a.id)) void fetchLocalFile(a.id, a.name ?? "file", a.mime ?? "application/octet-stream");
    } else {
      tempChats.put({ ...chat, messages });
    }
  }, [gen, liveId, chat, save]);

  const messages = (chat?.messages ?? []).map((m) => (m.id === liveId && gen ? { ...m, parts: gen.parts, status: gen.status, error: gen.error } : m));
  const streaming = messages.some((m) => m.status === "streaming");
  const lastLen = messages.length ? JSON.stringify(messages[messages.length - 1].parts).length : 0;
  const { ref, toBottom } = useStickToBottom(`${messages.length}:${lastLen}`);

  async function send(text: string) {
    if (!chat) return false;
    toBottom();
    const parts: Part[] = [...(text ? [{ type: "text" as const, text }] : []), ...pending.parts];
    const next = { ...chat, messages: [...chat.messages, { id: newId(), role: "user" as const, parts, status: "done" as const }] };
    pending.clear();
    await save(next);
    await generate(next);
  }

  async function editAt(index: number, text: string) {
    if (!chat) return;
    const old = chat.messages[index];
    const parts: Part[] = [{ type: "text", text }, ...old.parts.filter((p) => p.type === "attachment")];
    const next = { ...chat, messages: [...chat.messages.slice(0, index), { ...old, id: newId(), parts }] };
    await save(next);
    await generate(next);
  }

  async function regenerate(index: number) {
    if (!chat) return;
    const next = { ...chat, messages: chat.messages.slice(0, index) };
    await generate(next);
  }

  async function saveToServer() {
    if (!chat) return;
    try {
      const keep = chat.messages.filter((m) => m.status !== "streaming");
      // Saving moves the files kept in this browser to the server, like normal uploads.
      const ids: Record<string, string> = {};
      for (const fid of localIds(keep)) {
        const f = await localFiles.get(fid);
        if (f) ids[fid] = (await uploadFile(new File([f.blob], f.name, { type: f.mime }))).id;
      }
      const saved = await api.post<Chat>("/api/temp/save", {
        model_id: chat.model_id,
        settings: chat.mcp_servers ? { mcp_servers: chat.mcp_servers } : {},
        folder_id: chat.folder_id ?? null,
        system_prompt: chat.system_prompt ?? null,
        messages: keep.map((m) => ({ role: m.role, parts: replaceIds(m.parts, ids) })),
      });
      await localFiles.deleteMany(localIds(chat.messages));
      await tempChats.delete(chat.id);
      notifyChanged();
      qc.invalidateQueries({ queryKey: ["chats", "list"] });
      navigate(`/c/${saved.id}`, { replace: true });
    } catch (e) {
      setError(e);
    }
  }

  async function discard() {
    if (!confirm("Delete this temporary chat?")) return;
    if (chat) await localFiles.deleteMany(localIds(chat.messages));
    await tempChats.delete(id);
    notifyChanged();
    navigate("/", { replace: true });
  }

  if (chat === undefined)
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner size={24} />
      </div>
    );
  if (chat === null)
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-zinc-500">This temporary chat no longer exists in this browser.</div>
    );

  return (
    <div className="flex min-h-0 flex-1">
    <DropZone onFiles={pending.add} className="flex min-h-0 min-w-0 flex-1 flex-col">
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 pl-14 md:pl-4 dark:border-amber-900/60 dark:bg-amber-950/30">
        <EyeOff size={16} className="text-amber-700 dark:text-amber-400" />
        <h1 className="truncate text-sm font-medium">Temporary chat</h1>
        <span className="hidden text-xs text-zinc-500 sm:inline">kept only in this browser</span>
        <div className="ml-auto flex gap-1">
          <PrivateToggle on={!!chat.private} onChange={(v) => save({ ...chat, private: v })} />
          <Button size="sm" variant="secondary" onClick={saveToServer} disabled={streaming}>
            <Save size={14} /> Save chat
          </Button>
          <Button size="sm" variant="ghost" onClick={discard} aria-label="Delete">
            <Trash2 size={14} />
          </Button>
        </div>
      </header>
      <div ref={ref} className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl space-y-6 px-4 py-6">
          {messages.map((m, i) => (
            <MessageView
              key={m.id}
              message={m}
              busy={streaming}
              renderers={chatRenderers}
              onEdit={m.role === "user" ? (text) => text.trim() && editAt(i, text) : undefined}
              onRegenerate={m.role === "assistant" ? () => regenerate(i) : undefined}
            />
          ))}
        </div>
      </div>
      <div className="mx-auto w-full max-w-3xl px-4 pb-4">
        <ErrorText error={error} />
        <Composer
          streaming={streaming}
          onStop={async () => {
            const streamingId = liveId ?? messages.find((m) => m.status === "streaming")?.id;
            if (!streamingId) return;
            try {
              await api.post(`/api/messages/${streamingId}/stop`);
            } catch {
              // Nothing running on the server any more: settle the message from what the server knows.
              const c = await tempChats.get(id);
              if (c) await recover(c);
            }
          }}
          onSend={send}
          disabled={!modelId || pending.uploading}
          context={ctxLen ? { used: usedTokens(chat.messages), length: ctxLen } : null}
          canSendEmpty={pending.parts.length > 0}
          onPasteFiles={pending.add}
          attachments={<PendingFiles files={pending.files} onRemove={pending.remove} />}
          toolbar={
            <>
              <AttachButton onFiles={pending.add} />
              <ToolsPicker value={chat.mcp_servers} onChange={(ids) => save({ ...chat, mcp_servers: ids })} />
              <FolderPicker value={chat.folder_id} onChange={(f) => save({ ...chat, folder_id: f })} />
              <ModelSelect value={modelId} onChange={setModel} />
            </>
          }
        />
      </div>
    </DropZone>
    <ArtifactPanel all={collectArtifacts(messages)} />
    </div>
  );
}
